import React, { useMemo } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { Control, useWatch } from "react-hook-form"
import { DioboxEvent, TextMessage, WhatsAppTemplate } from "sharedTypes"
import Icon from "components/elements/Icon"
import InlineWarning from "components/elements/InlineWarning"
import QRCodePlaceholder from "components/elements/QRCodePlaceholder"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import { useForms } from "queries/forms"
import { getFormsGroupedByType } from "utilities/forms"
import { hasVariableValue, joinPunctuation, parseTemplatePreview } from "utilities/whatsappTemplates"
import VariablePill, { FormGroup } from "./VariablePill"

type Props = {
  control: Control<any>
  contentSid: string
  event: DioboxEvent
  textMessage: TextMessage
  onSave?: () => void
}

const TemplateEditor = ({
  control, contentSid, event, textMessage, onSave,
}: Props) => {
  const { template } = useWhatsAppTemplate(contentSid)
  const { data: forms } = useForms(event.id)

  const segments = useMemo(
    () => (template ? parseTemplatePreview(template.preview, template.variables) : []),
    [template],
  )
  const groupedForms: FormGroup[] = useMemo(
    () => (forms?.data ? getFormsGroupedByType(forms.data, event) : []),
    [forms?.data, event],
  )

  if (!template) return null

  return (
    <Box
      mt={4}
      p={4}
      bg="gray.25"
      borderRadius="md"
      borderWidth={1}
      fontSize="md"
      lineHeight={1.75}
      whiteSpace="pre-wrap"
    >
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return <Text as="span" key={`text-${i}`}>{joinPunctuation(segment.content, i)}</Text>
        }

        return (
          <VariablePill
            key={`var-${segment.index}`}
            variable={segment.variable}
            name={`whatsappContentVariables.${segment.index + 1}`}
            control={control}
            forms={groupedForms}
            size="md"
            onSave={onSave}
            eventId={event.id}
            textMessageId={textMessage.externalId}
          />
        )
      })}
    </Box>
  )
}

type AttachmentsProps = Omit<Props, "contentSid"> & {
  template: WhatsAppTemplate
}

export const TemplateAttachments = ({
  control, template, event, textMessage, onSave,
}: AttachmentsProps) => {
  const watchedVariables = useWatch({
    control, name: "whatsappContentVariables",
  }) as Record<string, string> | undefined

  const unreferencedVariables = useMemo(() => template.variables
    .map((variable, index) => ({ variable, index }))
    .filter(({ variable, index }) => {
      const placeholder = `{{${index + 1}}}`
      if (template.preview.includes(placeholder)) return false

      return variable.type === "media"
        || variable.mergeTag === "GUEST_QR_CODE_IMAGE"
    }), [template])

  if (unreferencedVariables.length === 0) return null

  return (
    <>
      {unreferencedVariables.map(({ variable, index }) => {
        const isQRCode = variable.mergeTag === "GUEST_QR_CODE_IMAGE"

        if (isQRCode) {
          return (
            <Flex key={index} alignItems="flex-start">
              <Text fontWeight="bold" w="25%" pt={1}>Attachment</Text>
              <QRCodeAttachment label={variable.label} />
            </Flex>
          )
        }

        const helpText = variable.mediaType === "document"
          ? "This template supports document files (PDF, DOC, DOCX) up to 5 MB."
          : "This template supports JPEG and PNG images up to 5 MB."

        const isMissing = !hasVariableValue(watchedVariables?.[(index + 1).toString()])

        return (
          <Box key={index}>
            <Flex alignItems="flex-start">
              <Text fontWeight="bold" w="25%" pt={1}>Attachment</Text>
              <VariablePill
                variable={variable}
                name={`whatsappContentVariables.${index + 1}`}
                control={control}
                size="md"
                onSave={onSave}
                eventId={event.id}
                textMessageId={textMessage.externalId}
              />
            </Flex>
            {isMissing && (
              <InlineWarning ml="25%">Not uploaded</InlineWarning>
            )}
            <Text fontSize="sm" ml="25%" mt={1}>
              {helpText}
            </Text>
          </Box>
        )
      })}
    </>
  )
}

export const TemplateButtons = ({ template }: { template: WhatsAppTemplate }) => {
  if (!template.buttons?.length) return null

  return (
    <Flex alignItems="flex-start">
      <Text fontWeight="bold" w="25%" pt={1}>Quick Reply</Text>
      <Flex alignItems="center" gap={1.5} cursor="not-allowed">
        <Flex
          bg="gray.25"
          borderWidth={1}
          borderColor="gray.300"
          borderRadius="md"
        >
          {template.buttons.map((label, i) => (
            <Box
              key={label}
              px={3}
              py={1}
              borderLeftWidth={i > 0 ? 1 : 0}
              borderColor="gray.300"
            >
              <Text fontSize="sm" color="gray.600" fontWeight="medium">{label}</Text>
            </Box>
          ))}
        </Flex>
        <Icon icon="lock" size={2.5} color="gray.500" />
      </Flex>
    </Flex>
  )
}

const QRCodeAttachment = ({ label }: { label: string }) => (
  <Box
    w="120px"
    bg="gray.25"
    borderWidth={1}
    borderColor="gray.300"
    borderRadius="md"
    overflow="hidden"
    cursor="not-allowed"
  >
    <Box p={1.5}>
      <QRCodePlaceholder />
    </Box>
    <Flex alignItems="center" gap={1.5} px={2} pb={2}>
      <Icon icon="lock" size={3} color="gray.500" />
      <Text fontSize="xs" color="gray.600" fontWeight="medium">{label}</Text>
    </Flex>
  </Box>
)

export default TemplateEditor
